import React from 'react'
import { LineItem, PriceTag } from 'msp-components'
import { getAssetUrl } from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {
  useWindowSize,
  productUrlBuilder,
  useMultiSite
} from 'msp-integrations'
import useProduct from '@/hooks/use-product'
import JasminCommerceBox from '@/components/molecules/JasminCommerceBox/JasminCommerceBox'
import * as T from './LineItemProduct.types'

const LineItemProductDisplay = ({
  type,
  product,
  removePromoCode,
  couponItems,
  showPromotions = false,
  onClick,
  isWishlistPage,
  onWishlistChange,
  handleAddToCart
}: T.LineItemProductDisplayProps) => {
  const { buildUrl } = useMultiSite()
  const {
    id,
    name,
    productId,
    productName,
    image,
    c_image,
    quantity,
    priceAfterItemDiscount,
    priceAdjustments,
    c_priceDiscounted,
    c_basePrice,
    price,
    c_minSetPrice,
    currency,
    basketPrice,
    c_servings,
    c_purchasable,
    shortDescription,
    c_primaryCategoryName,
    c_primaryCategoryPath,
    c_classCategoryName,
    c_classCategoryPath,
    c_welcomeOfferPart
  } = product

  //calculate priceAfterItemDiscount without Customer Credit adjusment
  const validDiscounts = priceAdjustments?.filter(
    (adjustment) => !adjustment?.promotionId?.startsWith('CustomerCredit-')
  )
  const priceWithDiscounts = validDiscounts?.reduce(
    (acc, current) => acc + current.price,
    price
  )

  let ats, orderable, preorderable, stockLevel, inStockDate
  if (product?.c_inventory) {
    ats = product?.c_inventory?.ats || 0
    orderable = product?.c_inventory?.orderable || false
    preorderable = product?.c_inventory?.preorderable || false
    stockLevel = product?.c_inventory?.stockLevel || 0
    inStockDate =
      product?.c_inventory?.inStockDate || '9999-12-31T00:00:00.000Z'
  }

  const fallBackImage = getAssetUrl('static/img/fallback-product-image.png')
  const { width, breakpoints } = useWindowSize()

  return (
    <LineItem
      id={productId || id}
      productDescription={c_servings || shortDescription}
      productName={name || productName}
      purchasable={c_purchasable}
      thumbnailUrl={c_image || image?.disBaseLink || fallBackImage}
      fallBackImage={fallBackImage}
      priceAdjustments={showPromotions && priceAdjustments}
      priceAfterItemDiscount={priceWithDiscounts}
      removePromoCode={removePromoCode}
      couponsItems={couponItems}
      currency={currency}
      productUrl={buildUrl(
        productUrlBuilder({
          id: productId || id,
          name: name || productName,
          classCategoryName: c_classCategoryName,
          classCategoryPath: c_classCategoryPath,
          primaryCategoryName: c_primaryCategoryName,
          primaryCategoryPath: c_primaryCategoryPath
        })
      )}
      inventory={{ ats, orderable, preorderable, stockLevel, inStockDate }}
      inline={width && width >= breakpoints.lg}
      commerceBox={
        type &&
        type !== 'overview' &&
        price >= 0 && (
          <JasminCommerceBox
            data={{
              product: product,
              type: type
            }}
            isWishlistPage={isWishlistPage}
            onWishlistChange={onWishlistChange}
            handleAddToCart={handleAddToCart}
          />
        )
      }
      overview={type === 'overview'}
      priceTag={
        type === 'overview' && (
          <PriceTag
            className='inline pl-4'
            price={basketPrice || c_minSetPrice || price}
            c_basePrice={c_basePrice}
            discountPrice={priceWithDiscounts}
            currency={currency}
          />
        )
      }
      productQuantity={quantity}
      onClick={onClick}
      isWelcomeOfferPart={c_welcomeOfferPart || false}
    />
  )
}

const LineItemProduct = ({
  type,
  product,
  productId,
  removePromoCode,
  couponItems,
  showPromotions = false,
  extendProduct = false,
  onClick,
  isWishlistPage,
  onWishlistChange,
  handleAddToCart
}: T.LineItemProductProps) => {
  const productParsed = useProduct(
    (extendProduct ? productId : product) || productId
  )

  return (
    !productParsed.showLoading &&
    !product?.isError && (
      <LineItemProductDisplay
        type={type}
        product={{ ...productParsed, ...product }}
        couponItems={couponItems}
        showPromotions={showPromotions}
        removePromoCode={removePromoCode}
        onClick={onClick}
        isWishlistPage={isWishlistPage}
        onWishlistChange={onWishlistChange}
        handleAddToCart={handleAddToCart}
      />
    )
  )
}

export default LineItemProduct
